import { useEffect, useState } from 'react'
import { useConnectivityStore, formatOffline } from '@/features/connectivity/connectivity.store'
import { queryClient } from '@/lib/query-client'
import { DsIconGlyph, DsIcons, DsText } from '@/ds'
import { connectivityStrings } from '@/features/connectivity/strings'

// Mount once in App: starts the ping loop, refreshes stale reads on reconnect, renders the banner (first miss)
// or the blocking overlay (offline 8 s or more).
export function Connectivity() {
  const status = useConnectivityStore((s) => s.status)
  const offlineSince = useConnectivityStore((s) => s.offlineSince)
  useEffect(() => {
    const store = useConnectivityStore.getState()
    store.start()
    const off = store.onRestored(() => void queryClient.invalidateQueries())
    return () => {
      off()
      store.stop()
    }
  }, [])
  useEffect(() => {
    document.body.style.pointerEvents = status === 'offlineOverlay' ? 'none' : ''
    return () => {
      document.body.style.pointerEvents = ''
    }
  }, [status])
  if (status === 'online') return null
  if (status === 'offlineBanner') {
    return (
      <div role="status" className="fixed inset-x-0 bottom-16 z-40 flex h-10 items-center justify-center gap-2 bg-foreground text-background md:bottom-0">
        <DsIconGlyph icon={DsIcons.wifiOff} size={16} />
        <span className="text-small-strong">{connectivityStrings.banner}</span>
      </div>
    )
  }
  return <OfflineOverlay since={offlineSince ?? Date.now()} />
}

function OfflineOverlay({ since }: { since: number }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])
  return (
    <div role="alertdialog" aria-modal="true" aria-labelledby="offline-title" className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-3 bg-scrim px-gutter text-center backdrop-blur-sm animate-ds-fade-in" style={{ pointerEvents: 'auto' }}>
      <span className="relative inline-flex size-20 items-center justify-center rounded-full bg-card text-foreground">
        <span aria-hidden className="absolute inset-0 rounded-full border-2 border-brand-vivid/40" style={{ animation: 'ds-pulse-scale 2s ease-in-out infinite' }} />
        <DsIconGlyph icon={DsIcons.wifiOff} size={32} />
      </span>
      <DsText variant="title2" as="h2" id="offline-title" className="text-white">{connectivityStrings.title}</DsText>
      <DsText variant="body" className="text-white/80">{connectivityStrings.resume}</DsText>
      <DsText variant="caption" className="text-white/60 tabular-nums">{connectivityStrings.offlineFor(formatOffline(now - since))}</DsText>
    </div>
  )
}
